import type {Image as GRPCImageType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/image.types';
import type {Video as GRPCVideoType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/video.types';
import {
  PageItem_ImageTextContentBlock,
  PageItem_ImageTextContentBlock_Layout,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/story_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions} from '@aboutyou/tracking';
import {
  Button,
  COLORS,
  ContentBlockImageTile,
  ContentBlockVideoAutoPlay,
  FONT_SIZES,
  FONT_WEIGHTS,
  GRID_GUTTER_WIDTH,
  ImageRatio,
  RADIUS,
  SPACING,
  UnstyledLink,
  gridContainer,
  mediaTabletAndUp,
  mobileOnlyLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';

const FALLBACK_MEDIA_RATIO: ImageRatio = '3:4' as ImageRatio;

const toRatio = (
  aspectRatio: {width: number; height: number} | undefined,
): ImageRatio | undefined =>
  aspectRatio
    ? (`${aspectRatio.width}:${aspectRatio.height}` as ImageRatio)
    : undefined;

/** The poster must match the video's ratio (CMS-enforced, Requirements 5.2), so it's
 * derived from the video's own ratio rather than read independently off the image. */
const buildVideoProps = (
  video: GRPCVideoType,
  posterOverride: GRPCImageType | undefined,
) => {
  const aspectRatio = toRatio(video.aspectRatio) ?? FALLBACK_MEDIA_RATIO;
  const poster = posterOverride ?? video.fallbackImage;

  return {
    sources: video.sources,
    lowSources: video.lowSources,
    description: video.description,
    aspectRatio,
    fallbackImage: {
      url: poster.url,
      description: poster.description,
      aspectRatio,
    },
  };
};

/**
 * A text column (headline, body copy) beside the media column on tablet and up,
 * stacked below it on mobile; the media may be an image or a video. Copy lives
 * outside the media frame, unlike `StoryImageTile`, which overlays its copy on
 * the image.
 */
export const StoryImageText: React.FC<{
  content: PageItem_ImageTextContentBlock;
  streamPosition: number;
  testId?: string;
}> = ({content, streamPosition, testId = 'storyImageText'}) => {
  const {t} = useTranslation();
  const {layout, headline, bodyText, cta, media, promotionTracker} = content;

  const video = media.videos?.video;

  const ctaUrl = cta.linkTarget?.url;
  const ctaButton =
    cta.show && ctaUrl
      ? {text: cta.text || t('Discover more'), url: ctaUrl}
      : undefined;
  // show=false + a link makes the media tappable instead of showing a button.
  const mediaHref = !cta.show && ctaUrl ? ctaUrl : undefined;

  const mediaTile = video ? (
    <ContentBlockVideoAutoPlay
      testId={`${testId}Media`}
      // When both are present the image is the static fallback (poster).
      video={buildVideoProps(video, media.image)}
      disablePlayPauseControl
      disableMuteUnmuteControl
    />
  ) : (
    media.image && (
      <ContentBlockImageTile
        testId={`${testId}Media`}
        image={{
          url: media.image.url,
          description: media.image.description,
          aspectRatio: toRatio(media.image.aspectRatio) ?? FALLBACK_MEDIA_RATIO,
        }}
      />
    )
  );

  return (
    <Wrapper
      data-testid={testId}
      data-image-first={
        layout === PageItem_ImageTextContentBlock_Layout.IMAGE_TEXT
      }
      promotion={{
        source: TrackingPromotionCreative.GenericPromotion,
        promotionTracker,
      }}
      streamPosition={streamPosition}
    >
      <TextColumn>
        {headline && (
          <Headline data-testid={`${testId}Headline`}>{headline}</Headline>
        )}
        {bodyText && (
          <BodyText data-testid={`${testId}Text`}>{bodyText}</BodyText>
        )}
      </TextColumn>
      <MediaColumn>
        <Frame>
          {mediaHref ? (
            <UnstyledLink
              to={mediaHref}
              aria-label={video ? video.description : media.image?.description}
              data-testid={`${testId}Link`}
            >
              {mediaTile}
            </UnstyledLink>
          ) : (
            mediaTile
          )}
          {ctaButton && (
            <MediaCta>
              <CtaButton
                href={ctaButton.url}
                variant="glassy"
                size="fit-width"
                data-testid={`${testId}CTA`}
              >
                {ctaButton.text}
              </CtaButton>
            </MediaCta>
          )}
        </Frame>
      </MediaColumn>
    </Wrapper>
  );
};

const Wrapper = styled(TrackPromotions)`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${mobileOnlyLinaria(`gap: ${SPACING['Y.Md']};`)}

  &[data-image-first='true'] {
    flex-direction: column-reverse;
  }

  @media ${mediaTabletAndUp} {
    ${gridContainer}
    flex-direction: row;

    &[data-image-first='true'] {
      flex-direction: row-reverse;
    }
  }
`;

/** The inset is against the image, not the page edge, so it applies at every width. */
const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING['Y.Md']};
  width: 100%;
  padding-inline: ${SPACING['X.Lg']};
  text-align: center;

  @media ${mediaTabletAndUp} {
    min-width: 0;
    align-items: flex-start;
    gap: ${SPACING['Y.Lg']};
    padding-block: ${SPACING['Y.Xl']};
    text-align: left;
    width: calc((100% - ${GRID_GUTTER_WIDTH}) / 2);
  }
`;

const MediaColumn = styled.div`
  width: 100%;

  @media ${mediaTabletAndUp} {
    width: calc((100% - ${GRID_GUTTER_WIDTH}) / 2);
  }
`;

const Frame = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: ${RADIUS.Sm};
`;

const Headline = styled.h2`
  margin: 0;
  font-size: ${FONT_SIZES.px32};
  font-weight: ${FONT_WEIGHTS.w700};
  line-height: 40px;
  letter-spacing: 0;
  color: ${COLORS.Black};
  word-break: break-word;

  @media ${mediaTabletAndUp} {
    font-size: ${FONT_SIZES.px40};
    line-height: 44px;
  }
`;

const BodyText = styled.p`
  margin: 0;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.w400};
  line-height: 20px;
  color: ${COLORS.Black};
  white-space: pre-line;
`;

const MediaCta = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 0 ${SPACING['X.Lg']} ${SPACING['Y.Xl']};
  pointer-events: none;
`;

const CtaButton = styled(Button)`
  pointer-events: auto;
`;
